import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Users } from './pages/Users';
import { Trademarks } from './pages/Trademarks';
import { TrademarkDetail } from './pages/TrademarkDetail';
import { Clients } from './pages/Clients';
import { ClientDetail } from './pages/ClientDetail';
import { ProcedureDetail } from './pages/ProcedureDetail';
import { Alerts } from './pages/Alerts';
import { Settings } from './pages/Settings';
import { RiskDashboard } from './pages/Risk/RiskDashboard';
import { RiskWizard } from './pages/Risk/RiskWizard';
import { RiskReportDetail } from './pages/Risk/RiskReportDetail';
// import { Reports } from './pages/Reports'; // Deprecated
import { ReportsContainer } from './components/reports/ReportsContainer';
import { ReportsLanding } from './pages/reports/ReportsLanding';
import { ExecutiveReport } from './pages/reports/ExecutiveReport';
import { PortfolioReport } from './pages/reports/PortfolioReport';
import { MonitoringReport } from './pages/reports/MonitoringReport';
import { ProceduresReport } from './pages/reports/ProceduresReport';
import { BrandProtectionReport } from './pages/reports/BrandProtectionReport';
import { ClientReportBuilder } from './pages/reports/ClientReportBuilder';
import { Procedures } from './pages/Procedures';
import { GazetteEvents } from './pages/GazetteEvents';
import { GazetteRules } from './pages/GazetteRules';
import { SharedLawFirmsList } from './pages/shared/SharedLawFirmsList';
import { SharedLawFirmDetail } from './pages/shared/SharedLawFirmDetail';
import { PlatformOverview } from './pages/platform/PlatformOverview';
import { PlatformTenants } from './pages/platform/PlatformTenants';
import { PlatformUsersGlobal } from './pages/platform/PlatformUsersGlobal';
import { PlatformPlans } from './pages/platform/PlatformPlans';
import { PlatformLinks } from './pages/platform/PlatformLinks';
import { Layout } from './components/Layout';
import { Toaster } from './components/ui/sonner';



import { JobProvider } from './contexts/JobContext';
import { ImportQueueProvider } from './contexts/ImportQueueContext';

const AppRoutes = () => {
  const { user, isEndUser, isCompany, isAdmin, can } = useAuth();

  return (
    <JobProvider>
      <ImportQueueProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          {/* DASHBOARD */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout><Dashboard /></Layout>
              </ProtectedRoute>
            }
          />

          {/* TRADEMARKS */}
          {can('trademarks') && (
            <>
              <Route
                path="/trademarks"
                element={
                  <ProtectedRoute>
                    <Layout><Trademarks /></Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/trademarks/:id"
                element={
                  <ProtectedRoute>
                    <Layout><TrademarkDetail /></Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/risk"
                element={
                  <ProtectedRoute>
                    <Layout><RiskDashboard /></Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/risk/new"
                element={
                  <ProtectedRoute>
                    <Layout><RiskWizard /></Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/risk/report/:jobId"
                element={
                  <ProtectedRoute>
                    <Layout><RiskReportDetail /></Layout>
                  </ProtectedRoute>
                }
              />
            </>
          )}

          {/* CLIENTS */}
          {!isEndUser() && can('clients') && (
            <>
              <Route
                path="/clients"
                element={
                  <ProtectedRoute>
                    <Layout><Clients /></Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/clients/:id"
                element={
                  <ProtectedRoute>
                    <Layout><ClientDetail /></Layout>
                  </ProtectedRoute>
                }
              />
            </>
          )}

          {/* PROCEDURES */}
          {!isEndUser() && can('procedures') && (
            <>
              <Route
                path="/procedures"
                element={
                  <ProtectedRoute>
                    <Layout><Procedures /></Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/procedures/:id"
                element={
                  <ProtectedRoute>
                    <Layout><ProcedureDetail /></Layout>
                  </ProtectedRoute>
                }
              />
            </>
          )}

          {/* ALERTS */}
          {can('alerts') && (
            <Route
              path="/alerts"
              element={
                <ProtectedRoute>
                  <Layout><Alerts /></Layout>
                </ProtectedRoute>
              }
            />
          )}

          {/* GAZETTE */}
          {can('gazette') && (
            <>
              <Route
                path="/gazette"
                element={
                  <ProtectedRoute>
                    <Layout><GazetteEvents /></Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gazette/rules"
                element={
                  <ProtectedRoute>
                    <Layout><GazetteRules /></Layout>
                  </ProtectedRoute>
                }
              />
            </>
          )}

          {/* REPORTS */}
          {!isEndUser() && can('reports') && (
            <Route path="/reports" element={
              <ProtectedRoute>
                <Layout><ReportsContainer /></Layout>
              </ProtectedRoute>
            }>
              <Route index element={<ReportsLanding />} />
              <Route path="executive" element={<ExecutiveReport />} />
              <Route path="portfolio" element={<PortfolioReport />} />
              <Route path="monitoring" element={<MonitoringReport />} />
              <Route path="procedures" element={<ProceduresReport />} />
              <Route path="brand-protection" element={<BrandProtectionReport />} />
              <Route path="client" element={<ClientReportBuilder />} />
            </Route>
          )}

          {/* USERS */}
          {isAdmin() && !isEndUser() && can('users') && (
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <Layout><Users /></Layout>
                </ProtectedRoute>
              }
            />
          )}

          {/* SETTINGS */}
          {isAdmin() && (
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Layout><Settings /></Layout>
                </ProtectedRoute>
              }
            />
          )}

          {/* SHARED LAW FIRMS (B2B) */}
          {isCompany() && can('lawfirms') && (
            <>
              <Route
                path="/shared/lawfirms"
                element={
                  <ProtectedRoute>
                    <Layout><SharedLawFirmsList /></Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/shared/lawfirms/:id"
                element={
                  <ProtectedRoute>
                    <Layout><SharedLawFirmDetail /></Layout>
                  </ProtectedRoute>
                }
              />
            </>
          )}

          {/* PLATFORM ROUTES */}
          <Route
            path="/platform"
            element={<Navigate to="/platform/overview" replace />}
          />
          <Route
            path="/platform/overview"
            element={
              <ProtectedRoute roles={['SUPER_ADMIN']}>
                <Layout><PlatformOverview /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/platform/tenants"
            element={
              <ProtectedRoute roles={['SUPER_ADMIN']}>
                <Layout><PlatformTenants /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/platform/users"
            element={
              <ProtectedRoute roles={['SUPER_ADMIN']}>
                <Layout><PlatformUsersGlobal /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/platform/plans"
            element={
              <ProtectedRoute roles={['SUPER_ADMIN']}>
                <Layout><PlatformPlans /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/platform/links"
            element={
              <ProtectedRoute roles={['SUPER_ADMIN']}>
                <Layout><PlatformLinks /></Layout>
              </ProtectedRoute>
            }
          />

          {/* Fallback for unknown routes */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </ImportQueueProvider>
    </JobProvider >
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
      <Toaster position="top-right" />
    </BrowserRouter>
  );
}

export default App;
